import NoDataRow from '@/platform/common/components/NoDataRow/NoDataRow';
import SearchBarFilter from '@/platform/common/components/SearchBarFilter/SearchBarFilter';
import SelectedCustomer from '@/platform/common/components/SelectedCustomer/SelectedCustomer';
import { useFilters } from '@/platform/hooks/useFilters';
import { formatMoney, localDate } from '@/platform/lib/utils';
import { useConfirmationStore } from '@/platform/stores/useConfirmationStore';
import {
    Button,
    Container,
    Loader,
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    MenuSeparator,
    Segment,
    SegmentContent,
    SegmentHeader,
    Table,
    TableBody,
    TableCell,
    TableFooter,
    TableHeader,
    TableHeaderCell,
    TablePlaceholderRow,
    TableRow,
} from '@/platform/ui';
import {
    EllipsisVerticalIcon,
    EyeIcon,
    PencilIcon,
    PlusIcon,
    TrashIcon,
} from '@heroicons/react/24/outline';
import {
    Deferred,
    InfiniteScroll,
    Link,
    router,
    usePage,
} from '@inertiajs/react';
import StatusIcon from './components/StatusIcon/StatusIcon';

const CustomerIndexView = () => {
    const { auth, invoices, totals } = usePage().props;
    const { updateFilters, isSorted } = useFilters(['invoices']);
    const confirm = useConfirmationStore(state => state.confirm);

    const destroy = invoice => {
        router.delete(
            `/customers/${invoice.customer_id}/invoices/${invoice.id}`,
            {
                preserveScroll: true,
                preserveState: true,
            }
        );
    };

    return (
        <Container fluid>
            <SelectedCustomer />

            <Segment>
                <SegmentHeader header='Facturen'>
                    {auth.permissions.includes('create invoices') && (
                        <Button
                            variant='primary'
                            as={Link}
                            href={`/customers/${auth.user.selected_customer_id}/invoices/create`}
                        >
                            <PlusIcon className='size-5' />
                            Nieuwe factuur
                        </Button>
                    )}
                </SegmentHeader>
                <SegmentContent>
                    <SearchBarFilter only={['invoices']} />

                    <InfiniteScroll
                        data='invoices'
                        itemsElement='#table-body'
                        startElement='#table-header'
                        endElement='#table-footer'
                    >
                        {({ loadingNext, loadingPrevious }) => (
                            <Table striped>
                                <TableHeader id='table-header'>
                                    <TableRow>
                                        <TableHeaderCell collapsing />
                                        <TableHeaderCell
                                            sorted={isSorted('number')}
                                            onClick={() =>
                                                updateFilters('sort', 'number')
                                            }
                                        >
                                            Nummer
                                        </TableHeaderCell>
                                        <TableHeaderCell
                                            sorted={isSorted('created_at')}
                                            onClick={() =>
                                                updateFilters(
                                                    'sort',
                                                    'created_at'
                                                )
                                            }
                                        >
                                            Datum
                                        </TableHeaderCell>
                                        <TableHeaderCell
                                            sorted={isSorted('mailed_at')}
                                            onClick={() =>
                                                updateFilters(
                                                    'sort',
                                                    'mailed_at'
                                                )
                                            }
                                        >
                                            Verstuurd op
                                        </TableHeaderCell>
                                        <TableHeaderCell>
                                            Status
                                        </TableHeaderCell>
                                        <TableHeaderCell
                                            sorted={isSorted('reminder_at')}
                                            onClick={() =>
                                                updateFilters(
                                                    'sort',
                                                    'reminder_at'
                                                )
                                            }
                                        >
                                            Laatste reminder
                                        </TableHeaderCell>
                                        <TableHeaderCell>
                                            Reminders
                                        </TableHeaderCell>
                                        <TableHeaderCell textAlign='right'>
                                            Resultaat
                                        </TableHeaderCell>
                                        <TableHeaderCell textAlign='right'>
                                            Besparing klant
                                        </TableHeaderCell>
                                        <TableHeaderCell textAlign='right'>
                                            Factuurbedrag
                                        </TableHeaderCell>
                                        <TableHeaderCell collapsing />
                                    </TableRow>
                                </TableHeader>

                                <TableBody id='table-body'>
                                    {loadingPrevious && (
                                        <TableRow>
                                            <TableCell
                                                colSpan={11}
                                                textAlign='center'
                                            >
                                                <Loader />
                                            </TableCell>
                                        </TableRow>
                                    )}

                                    {invoices?.meta?.total === 0 && (
                                        <NoDataRow numberOfColumns={11} />
                                    )}

                                    {invoices?.data?.map(invoice => (
                                        <TableRow key={invoice.id}>
                                            <TableCell>
                                                <StatusIcon invoice={invoice} />
                                            </TableCell>

                                            <TableCell>
                                                <Link
                                                    href={`/customers/${invoice.customer_id}/invoices/${invoice.id}`}
                                                >
                                                    {invoice.number}
                                                </Link>
                                            </TableCell>
                                            <TableCell>
                                                {localDate(invoice.created_at)}
                                            </TableCell>
                                            <TableCell>
                                                {localDate(invoice.mailed_at) ||
                                                    '-'}
                                            </TableCell>
                                            <TableCell>
                                                {invoice.status_name}
                                            </TableCell>
                                            <TableCell>
                                                {localDate(
                                                    invoice.reminder_at
                                                ) || '-'}
                                            </TableCell>
                                            <TableCell>
                                                {invoice.reminders || '-'}
                                            </TableCell>
                                            <TableCell textAlign='right'>
                                                {formatMoney(
                                                    invoice.results || 0
                                                )}
                                            </TableCell>
                                            <TableCell textAlign='right'>
                                                {formatMoney(
                                                    invoice.customer_saving
                                                ) || '-'}
                                            </TableCell>
                                            <TableCell textAlign='right'>
                                                {formatMoney(
                                                    invoice.subtotal
                                                ) || '-'}
                                            </TableCell>
                                            <TableCell textAlign='right'>
                                                {[
                                                    'view invoices',
                                                    'delete invoices',
                                                    'update invoices',
                                                ]?.some(permission =>
                                                    auth.permissions.includes(
                                                        permission
                                                    )
                                                ) && (
                                                    <Menu>
                                                        <MenuButton>
                                                            <EllipsisVerticalIcon className='size-5' />
                                                        </MenuButton>
                                                        <MenuItems anchor='bottom end'>
                                                            {auth.permissions.includes(
                                                                'view invoices'
                                                            ) && (
                                                                <MenuItem
                                                                    as={Link}
                                                                    href={`/customers/${invoice.customer_id}/invoices/${invoice.id}`}
                                                                    icon={
                                                                        EyeIcon
                                                                    }
                                                                >
                                                                    Bekijken
                                                                </MenuItem>
                                                            )}
                                                            {auth.permissions.includes(
                                                                'update invoices'
                                                            ) && (
                                                                <MenuItem
                                                                    as={Link}
                                                                    href={`/customers/${invoice.customer_id}/invoices/${invoice.id}/edit`}
                                                                    icon={
                                                                        PencilIcon
                                                                    }
                                                                >
                                                                    Wijzigen
                                                                </MenuItem>
                                                            )}
                                                            {auth.permissions.includes(
                                                                'delete invoices'
                                                            ) && (
                                                                <>
                                                                    <MenuSeparator />
                                                                    <MenuItem
                                                                        className='text-red-600'
                                                                        onClick={() =>
                                                                            confirm(
                                                                                {
                                                                                    description:
                                                                                        'Weet u zeker dat u deze factuur wilt verwijderen?',
                                                                                    onConfirm:
                                                                                        close => {
                                                                                            destroy(
                                                                                                invoice
                                                                                            );
                                                                                            close();
                                                                                        },
                                                                                }
                                                                            )
                                                                        }
                                                                        icon={
                                                                            TrashIcon
                                                                        }
                                                                    >
                                                                        Verwijderen
                                                                    </MenuItem>
                                                                </>
                                                            )}
                                                        </MenuItems>
                                                    </Menu>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}

                                    {loadingNext && (
                                        <TableRow>
                                            <TableCell
                                                colSpan={11}
                                                textAlign='center'
                                            >
                                                <Loader />
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                                <TableFooter id='table-footer'>
                                    <Deferred
                                        data={['totals']}
                                        fallback={
                                            <TablePlaceholderRow colSpan={11} />
                                        }
                                    >
                                        <TableRow>
                                            <TableCell colSpan={6} />
                                            <TableCell textAlign='right'>
                                                Totaal
                                            </TableCell>
                                            <TableCell textAlign='right'>
                                                {formatMoney(
                                                    totals?.lines_total || 0
                                                )}
                                            </TableCell>
                                            <TableCell textAlign='right'>
                                                {formatMoney(
                                                    totals?.saving || 0
                                                )}
                                            </TableCell>
                                            <TableCell textAlign='right'>
                                                {formatMoney(
                                                    totals?.total || 0
                                                )}
                                            </TableCell>
                                            <TableCell />
                                        </TableRow>
                                    </Deferred>
                                </TableFooter>
                            </Table>
                        )}
                    </InfiniteScroll>
                </SegmentContent>
            </Segment>
        </Container>
    );
};

export default CustomerIndexView;
